import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { LlmProvider } from '@cp3/shared';
import { useTargetUidPayload } from './useTargetUidPayload';

interface TestChatInput {
  provider: LlmProvider;
  model: string;
  systemPrompt: string;
  message: string;
  temperature: number;
  maxTokens: number;
}

/**
 * Test-chat reads per-user API keys server-side, so admin-target mode
 * forwards the target uid (otherwise the admin's own keys would be used).
 */
export function useTestChat() {
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: TestChatInput) => {
      const fn = httpsCallable<TestChatInput & { uid?: string }, { reply: string }>(
        functions,
        'testChat',
      );
      const r = await fn({ ...input, ...adminPayload });
      return r.data.reply;
    },
  });
}

/**
 * Test call dials FROM the operating user's configured Twilio/Telnyx
 * number — admin-target aware so impersonation routes through the
 * target's credentials, not the admin's.
 */
export function useTestCall() {
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: { toNumber?: string }) => {
      const fn = httpsCallable<typeof input & { uid?: string }, { sid?: string; status?: string }>(
        functions,
        'initiateTestCall',
      );
      const r = await fn({ ...input, ...adminPayload });
      return r.data;
    },
  });
}
